import type { AxiosInstance } from "axios";

import { toast } from "sonner";

import type {
	CreatePublicContractPayload,
	InitiatePublicContractPaymentPayload,
	PublicContract,
	PublicContractListResult,
	PublicContractPaymentResult,
	PublicContractStatus
} from "@/types/public-contract";

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import axios from "./axios";
import { createVendorProduct } from "./products-query";
import { usePrivateAxios } from "./use-private-axios";

function unwrapData<T = unknown>(payload: unknown): T {
	if (payload == null || typeof payload !== "object")
		return payload as T;
	const root = payload as Record<string, unknown>;
	if ("data" in root && root.data != null)
		return root.data as T;
	return payload as T;
}

function normalizeList(raw: unknown): PublicContractListResult {
	const data = unwrapData<any>(raw);
	if (Array.isArray(data)) {
		return {
			items: data,
			totalItems: data.length,
			totalPages: 1,
			currentPage: 1,
			itemsPerPage: data.length
		};
	}
	const items
		= data?.items
			?? data?.data
			?? data?.contracts
			?? data?.results
			?? [];
	return {
		items: Array.isArray(items) ? items : [],
		totalItems: Number(data?.totalItems ?? data?.meta?.totalItems ?? items.length) || 0,
		totalPages: Number(data?.totalPages ?? data?.meta?.totalPages ?? 1) || 1,
		currentPage: Number(data?.currentPage ?? data?.meta?.currentPage ?? 1) || 1,
		itemsPerPage: Number(data?.itemsPerPage ?? data?.meta?.itemsPerPage ?? 10) || 10
	};
}

function parseMaybeJson(value: unknown) {
	if (typeof value !== "string")
		return value;
	const trimmed = value.trim();
	if (!trimmed.startsWith("{") && !trimmed.startsWith("["))
		return value;
	try {
		return JSON.parse(trimmed);
	}
	catch {
		return value;
	}
}

function normalizeContract(raw: unknown): PublicContract {
	const data = unwrapData<any>(raw);
	let contract: any = data?.id
		? data
		: data?.contract ?? data?.publicContract ?? data;
	if (!contract || typeof contract !== "object")
		return contract as PublicContract;

	contract = { ...contract };
	if (contract.buyerInfo == null && data?.buyerInfo != null)
		contract.buyerInfo = data.buyerInfo;
	if (contract.buyer == null && data?.buyer != null)
		contract.buyer = data.buyer;

	contract.buyerInfo = parseMaybeJson(contract.buyerInfo);
	contract.buyer = parseMaybeJson(contract.buyer);

	// Some payloads nest contact fields on the contract root.
	if (
		(!contract.buyerInfo || typeof contract.buyerInfo !== "object")
		&& (contract.firstName || contract.email)
	) {
		contract.buyerInfo = {
			firstName: contract.firstName,
			lastName: contract.lastName,
			email: contract.email,
			phoneNumber: contract.phoneNumber,
			buyerState: contract.buyerState,
			buyerCity: contract.buyerCity,
			buyerAddress: contract.buyerAddress
		};
	}

	return contract as PublicContract;
}

function normalizePayment(raw: unknown): PublicContractPaymentResult {
	const data = unwrapData<any>(raw);
	return {
		...data,
		contract: data?.contract ?? data?.publicContract,
		paymentConfig: data?.paymentConfig ?? data?.payment?.paymentConfig ?? data?.config,
		paymentRef:
			data?.paymentRef
			?? data?.order?.paymentRef
			?? data?.payment?.paymentRef
			?? data?.paymentConfig?.reference
	};
}

export function buildCreatePublicContractFormData(payload: CreatePublicContractPayload) {
	const form = new FormData();
	form.append("title", payload.title);
	form.append("description", payload.description);
	form.append("buyerInfo", JSON.stringify(payload.buyerInfo));

	if (payload.products?.length) {
		form.append("products", JSON.stringify(payload.products));
	}

	if (payload.newProducts?.length) {
		const serializable = payload.newProducts.map(
			({ files: _files, tempId: _tempId, previewUrl: _previewUrl, ...rest }) => rest
		);
		form.append("newProducts", JSON.stringify(serializable));
		payload.newProducts.forEach((product) => {
			product.files?.forEach((file, index) => {
				form.append("files", file, file.name || `image${index}.jpg`);
			});
		});
	}

	if (payload.shippingDate)
		form.append("shippingDate", payload.shippingDate);
	if (payload.estimatedDeliveryDate)
		form.append("estimatedDeliveryDate", payload.estimatedDeliveryDate);
	if (payload.estimatedWeight != null)
		form.append("estimatedWeight", String(payload.estimatedWeight));
	if (payload.deliveryCost != null)
		form.append("deliveryCost", String(payload.deliveryCost));
	if (payload.withinCity != null)
		form.append("withinCity", String(payload.withinCity));
	if (payload.shipoutStateId)
		form.append("shipoutStateId", payload.shipoutStateId);
	if (payload.shipoutCityId)
		form.append("shipoutCityId", payload.shipoutCityId);
	if (payload.paymentOption)
		form.append("paymentOption", payload.paymentOption);

	return form;
}

async function fetchPublicContracts(
	privateAxios: AxiosInstance,
	params?: {
		page?: number;
		limit?: number;
		status?: PublicContractStatus | string | null;
		search?: string | null;
	}
) {
	const { data } = await privateAxios.get("/public-contract", {
		params: {
			page: params?.page,
			limit: params?.limit,
			status: params?.status || undefined,
			search: params?.search?.trim() || undefined
		}
	});
	return normalizeList(data);
}

async function fetchPublicContract(privateAxios: AxiosInstance, id: string) {
	const { data } = await privateAxios.get(`/public-contract/${id}`, {
		timeout: 20_000
	});
	return normalizeContract(data);
}

function apiErrorMessage(error: any, fallback: string) {
	const payload = error?.response?.data;
	const message = payload?.message ?? payload?.error ?? payload?.errors;
	if (Array.isArray(message))
		return message.filter(Boolean).join(". ") || fallback;
	if (typeof message === "string" && message.trim())
		return message;
	if (!error?.response && typeof error?.message === "string" && error.message.trim())
		return error.message;
	return fallback;
}

async function createPublicContract(privateAxios: AxiosInstance, payload: CreatePublicContractPayload) {
	const products = [...(payload.products ?? [])];

	// Public-contract `newProducts` hits a Paystack init bug on the API.
	// Create catalog products with the working store endpoint, then attach them as existing items.
	if (payload.newProducts?.length) {
		for (const np of payload.newProducts) {
			const created = await createVendorProduct(privateAxios, {
				name: np.name,
				description: np.description,
				categoryIds: np.categoryIds,
				price: np.price,
				quantity: np.quantity,
				currencyId: np.currencyId,
				files: np.files
			});
			if (!created?.id)
				throw new Error("Product was created but the response had no id");
			products.push({
				productId: created.id,
				quantity: np.contractQuantity,
				vendorDiscountedPrice: np.vendorDiscountedPrice
			});
		}
	}

	const form = buildCreatePublicContractFormData({
		...payload,
		products: products.length ? products : undefined,
		newProducts: undefined
	});
	const { data } = await privateAxios.post("/public-contract", form, {
		headers: { "Content-Type": false }
	});
	return normalizeContract(data);
}

async function cancelPublicContract(privateAxios: AxiosInstance, id: string) {
	const { data } = await privateAxios.patch(`/public-contract/${id}/cancel`);
	return unwrapData(data);
}

/**
 * Payment route is `/code/{contract_id}/payment`.
 * Backend looks up by contract UUID (`contract_id`), not the short share code.
 */
async function initiatePublicContractPayment(
	contractKey: string,
	payload: InitiatePublicContractPaymentPayload
) {
	const { data } = await axios.post(
		`/public-contract/code/${encodeURIComponent(contractKey)}/payment`,
		payload
	);
	return normalizePayment(data);
}

async function verifyPublicContractPayment(contractKey: string) {
	const { data } = await axios.get(
		`/public-contract/code/${encodeURIComponent(contractKey)}/payment/verify`
	);
	return unwrapData(data);
}

export function usePublicContracts(params?: {
	page?: number;
	limit?: number;
	status?: PublicContractStatus | string | null;
	search?: string | null;
}) {
	const privateAxios = usePrivateAxios();
	return useQuery({
		queryKey: ["public-contracts", params],
		queryFn: () => fetchPublicContracts(privateAxios, params),
		placeholderData: keepPreviousData
	});
}

export function usePublicContract(id?: string) {
	const privateAxios = usePrivateAxios();
	const queryClient = useQueryClient();
	return useQuery({
		enabled: !!id,
		queryKey: ["public-contract", id],
		queryFn: () => fetchPublicContract(privateAxios, id!),
		retry: 1,
		staleTime: 30_000,
		placeholderData: () => {
			if (!id)
				return undefined;
			const lists = queryClient.getQueriesData<PublicContractListResult>({
				queryKey: ["public-contracts"]
			});
			for (const [, list] of lists) {
				const match = list?.items?.find(item => item.id === id);
				if (match)
					return match;
			}
			return undefined;
		}
	});
}

export function useCreatePublicContract() {
	const privateAxios = usePrivateAxios();
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["create-public-contract"],
		mutationFn: (payload: CreatePublicContractPayload) =>
			createPublicContract(privateAxios, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["public-contracts"] });
			queryClient.invalidateQueries({ queryKey: ["products"] });
		},
		onError: (error: any) => {
			toast.error(apiErrorMessage(error, "Failed to create invoice"));
		}
	});
}

export function useCancelPublicContract() {
	const privateAxios = usePrivateAxios();
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["cancel-public-contract"],
		mutationFn: (id: string) => cancelPublicContract(privateAxios, id),
		onSuccess: () => {
			toast.success("Invoice cancelled");
			queryClient.invalidateQueries({ queryKey: ["public-contracts"] });
			queryClient.invalidateQueries({ queryKey: ["public-contract"] });
		},
		onError: (error: any) => {
			toast.error(apiErrorMessage(error, "Failed to cancel invoice"));
		}
	});
}

export function useInitiatePublicContractPayment() {
	return useMutation({
		mutationKey: ["initiate-public-contract-payment"],
		mutationFn: ({
			contractId,
			payload
		}: {
			contractId: string;
			payload: InitiatePublicContractPaymentPayload;
		}) => initiatePublicContractPayment(contractId, payload),
		onError: (error: any) => {
			toast.error(apiErrorMessage(error, "Failed to get payment details"));
		}
	});
}

export function useVerifyPublicContractPayment() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["verify-public-contract-payment"],
		mutationFn: (contractId: string) => verifyPublicContractPayment(contractId),
		onSuccess: (_data, contractId) => {
			toast.success("Payment status refreshed");
			queryClient.invalidateQueries({ queryKey: ["public-contract", contractId] });
			queryClient.invalidateQueries({ queryKey: ["public-contracts"] });
		},
		onError: (error: any) => {
			toast.error(apiErrorMessage(error, "Could not verify payment"));
		}
	});
}

export function getPublicContractCode(contract?: PublicContract | null) {
	return String(contract?.contractCode || contract?.code || "").trim();
}

export function getPublicContractItems(contract?: PublicContract | null) {
	const items
		= contract?.contractItems
			?? contract?.contractItem
			?? contract?.items
			?? contract?.products
			?? [];
	return Array.isArray(items) ? items : [];
}

export function getPublicContractItemName(item: any) {
	const product
		= item && typeof item.product === "object"
			? item.product
			: item && typeof item.productId === "object"
				? item.productId
				: null;
	return (
		String(item?.name || "").trim()
		|| String(item?.productName || "").trim()
		|| String(product?.name || "").trim()
		|| "Item"
	);
}

export function getPublicContractBuyerInfo(contract?: PublicContract | null) {
	if (!contract)
		return null;
	let buyerRaw: any
		= contract.buyerInfo
		?? contract.buyer
		?? (contract as any).buyerId
		?? null;
	buyerRaw = parseMaybeJson(buyerRaw);
	if (!buyerRaw || typeof buyerRaw !== "object")
		return null;
	return buyerRaw as Record<string, any>;
}

export function getPublicContractBuyerName(contract?: PublicContract | null) {
	const buyer = getPublicContractBuyerInfo(contract);
	if (!buyer) {
		return (
			String((contract as any)?.buyerName || "").trim()
			|| String((contract as any)?.buyerEmail || "").trim()
			|| ""
		);
	}
	const composed = [buyer.firstName, buyer.lastName].filter(Boolean).join(" ").trim();
	return (
		composed
		|| String(buyer.fullName || "").trim()
		|| String(buyer.email || "").trim()
		|| String((contract as any)?.buyerName || "").trim()
		|| String((contract as any)?.buyerEmail || "").trim()
		|| ""
	);
}

export function getPublicContractPaymentKey(contract?: PublicContract | null) {
	if (!contract)
		return "";
	// Payment initiate expects the contract UUID (path param: contract_id).
	return String(contract.id || "").trim() || getPublicContractCode(contract);
}

export function buildPaymentPayloadFromContract(contract: PublicContract) {
	const info = getPublicContractBuyerInfo(contract) || {};
	const email = String(info.email || (contract as any).buyerEmail || "").trim();
	const firstName = String(info.firstName || "").trim() || "Buyer";
	const lastName = String(info.lastName || "").trim() || "-";
	const rawPhone = String(info.phoneNumber || info.phone || "").trim();
	const phoneNumber = normalizePhoneForPayment(rawPhone) || "0000000000";
	if (!email)
		return null;
	return {
		email,
		firstName,
		lastName,
		phoneNumber,
		paymentOption: "safe_haven" as const,
		buyerState: info.buyerState ? String(info.buyerState) : undefined,
		buyerCity: info.buyerCity ? String(info.buyerCity) : undefined,
		buyerAddress: info.buyerAddress ? String(info.buyerAddress) : undefined
	};
}

function normalizePhoneForPayment(phone: string) {
	const trimmed = phone.trim();
	if (!trimmed)
		return "";
	const digits = trimmed.replace(/\D/g, "");
	if (!digits)
		return trimmed;
	if (digits.startsWith("234") && digits.length >= 13)
		return `+${digits}`;
	if (digits.startsWith("0") && digits.length >= 11)
		return `+234${digits.slice(1)}`;
	if (digits.length === 10 && /^[789]/.test(digits))
		return `+234${digits}`;
	if (trimmed.startsWith("+"))
		return `+${digits}`;
	return `+${digits}`;
}

export function getPublicContractTotal(contract?: PublicContract | null) {
	if (!contract)
		return null;
	const candidates = [
		contract.totalAmount,
		contract.amount,
		contract.itemsAmount,
		(contract as any).totalCost,
		(contract as any).orderAmount,
		(contract as any).grandTotal
	];
	for (const value of candidates) {
		if (value == null || value === "")
			continue;
		const n = Number(value);
		if (!Number.isNaN(n))
			return n;
	}
	const items = getPublicContractItems(contract);
	if (!items.length)
		return null;
	const itemsTotal = items.reduce((sum, item) => {
		const qty = Number(item.quantity ?? item.productQuantity ?? 0) || 0;
		const unit = Number(item.vendorDiscountedPrice ?? item.price ?? 0) || 0;
		const line = item.totalCost != null ? Number(item.totalCost) : qty * unit;
		return sum + (Number.isNaN(line) ? 0 : line);
	}, 0);
	const delivery = Number(contract.deliveryCost) || 0;
	const total = itemsTotal + delivery;
	return total > 0 ? total : null;
}

export const PUBLIC_INVOICE_PAYMENT_STORAGE_KEY = "intasida/public-invoice-payment";

export function stashPublicInvoicePayment(contractId: string, payment: PublicContractPaymentResult) {
	try {
		const prev = JSON.parse(sessionStorage.getItem(PUBLIC_INVOICE_PAYMENT_STORAGE_KEY) || "{}");
		prev[contractId] = payment;
		sessionStorage.setItem(PUBLIC_INVOICE_PAYMENT_STORAGE_KEY, JSON.stringify(prev));
	}
	catch {
		/* ignore */
	}
}

export function readStashedPublicInvoicePayment(contractId: string): PublicContractPaymentResult | null {
	try {
		const prev = JSON.parse(sessionStorage.getItem(PUBLIC_INVOICE_PAYMENT_STORAGE_KEY) || "{}");
		return prev[contractId] ?? null;
	}
	catch {
		return null;
	}
}
